export type LocalizedField = {
  source: string | null;
  target: string | null;
  missing?: boolean;
  stale?: boolean;
  source_kind?: 'manual' | 'auto' | null;
};

export type AiLoadingKey =
  | { type: 'training'; field: 'title' | 'description' }
  | { type: 'chapter'; chapterId: number }
  | { type: 'sequence'; sequenceId: number }
  | { type: 'sequence-media'; sequenceId: number; mediaId: number }
  | { type: 'sequence-media-name'; sequenceId: number; mediaId: number }
  | {
      type: 'subitem';
      sequenceId: number;
      subitemId: number;
      field: 'title' | 'subtitle' | 'body';
    }
  | {
      type: 'subitem-media';
      sequenceId: number;
      subitemId: number;
      mediaId: number;
    }
  | {
      type: 'subitem-media-name';
      sequenceId: number;
      subitemId: number;
      mediaId: number;
    };

export const matchesAiLoadingKey = (
  current: AiLoadingKey | null,
  check: AiLoadingKey
): boolean => {
  if (!current) return false;
  if (current.type !== check.type) return false;

  switch (current.type) {
    case 'training':
      return (check as typeof current).field === current.field;
    case 'chapter':
      return (check as typeof current).chapterId === current.chapterId;
    case 'sequence':
      return (check as typeof current).sequenceId === current.sequenceId;
    case 'sequence-media':
    case 'sequence-media-name':
      return (
        (check as typeof current).sequenceId === current.sequenceId &&
        (check as typeof current).mediaId === current.mediaId
      );
    case 'subitem':
      return (
        (check as typeof current).sequenceId === current.sequenceId &&
        (check as typeof current).subitemId === current.subitemId &&
        (check as typeof current).field === current.field
      );
    case 'subitem-media':
    case 'subitem-media-name':
      return (
        (check as typeof current).sequenceId === current.sequenceId &&
        (check as typeof current).subitemId === current.subitemId &&
        (check as typeof current).mediaId === current.mediaId
      );
    default:
      return false;
  }
};

export type MediaEntry = {
  id: number;
  type: 'Audio' | 'Video' | 'Picture';
  locale: string;
  script: LocalizedField;
  // Picture alt-text is returned as `name`
  name?: LocalizedField | null;
};

export type SubitemScript = {
  id: number;
  kind: string;
  position: number;
  title?: LocalizedField | null;
  subtitle?: LocalizedField | null;
  body?: LocalizedField | null;
  media?: MediaEntry[];
  subitems?: SubitemScript[];
};

export type SequenceScript = {
  id: number;
  position: number;
  title?: LocalizedField | null;
  media?: MediaEntry[];
  subitems: SubitemScript[];
};

export type ChapterScript = {
  id: number;
  position: number;
  title?: LocalizedField | null;
  sequences: SequenceScript[];
};

export type TrainingHeader = {
  title?: LocalizedField | null;
  description?: LocalizedField | null;
};

export type TrainingScript = {
  trainingId: number;
  sourceLocale: string;
  targetLocale: string;
  training: TrainingHeader;
  chapters: ChapterScript[];
};

export type ApiErrorResult = { error: true; message?: string; status?: number };

export type TranslationsByLocale = Record<string, string>;

export type TrainingTranslationsPatch = {
  title_translations?: TranslationsByLocale;
  description_translations?: TranslationsByLocale;
};

export type ChapterTranslationsPatch = {
  id: number;
  title_translations?: TranslationsByLocale;
};

export type MediaTranslationsPatch = {
  id: number;
  script_translations?: TranslationsByLocale;
  name_translations?: TranslationsByLocale;
};

export type SubitemTranslationsPatch = {
  id: number;
  title_translations?: TranslationsByLocale;
  subtitle_translations?: TranslationsByLocale;
  body_translations?: TranslationsByLocale;
  media?: MediaTranslationsPatch[];
};

export type SequenceTranslationsPatch = {
  id: number;
  title_translations?: TranslationsByLocale;
  media?: MediaTranslationsPatch[];
  subitems?: SubitemTranslationsPatch[];
};

export type TranslationUpdatePatch = {
  ai_translate?: boolean;
  training?: TrainingTranslationsPatch;
  chapters?: ChapterTranslationsPatch[];
  sequences?: SequenceTranslationsPatch[];
};

export type GetTrainingScriptResponse =
  | { script: TrainingScript }
  | ApiErrorResult;

export type UpdateTranslationsResponse =
  | (TranslationUpdatePatch & { error?: false | undefined })
  | ApiErrorResult;

export type GlobalPlaceholders = {
  images: {
    horizontal: number | null;
    vertical: number | null;
  };
  audio: number | null;
  video: number | null;
};

export type GetGlobalPlaceholdersResponse =
  | (GlobalPlaceholders & { error?: false })
  | ApiErrorResult;
